import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { Reveal } from './Reveal';
import { CREATIVE_TEAM, FULL_CREDITS_PDF_URL, PRODUCTION, PRODUCTION_STATEMENT } from '../data/credits';
const ABOUT_IMAGE = 'https://cdn.magicpatterns.com/uploads/5DgyWD971LowWTNwPjBJS6/doubles.jpg';
const PARAGRAPHS = ["Aldous Huxley's The Doors of Perception (1954) has shaped how we talk about consciousness for seventy years. It has never been staged. This production is the first adaptation.", "It is a full-length, non-verbal work for 13 dancers. Sixteen choreographic scenes move between full sensory immersion and reflective stillness: the body overwhelmed, the mind watching. Huxley's book switches without warning between raw perception and philosophical reflection, and the show follows the same rhythm. It tells a story about direct experience without a single spoken word.", 'Huxley argued that the brain does not produce consciousness but filters what he called "Mind at Large." In 2026, as AI models reason, remember and create, his question is urgent again: what separates real understanding from imitation?'];
export function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const prefersReducedMotion = useReducedMotion();
  const {
    scrollYProgress
  } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], ['-4%', '4%']);
  return <section id="about" aria-label="About the production" className="bg-ink" data-id="element-11" data-mp="1gheyo2tuaw8m">
      <div ref={sectionRef} className="relative flex min-h-screen w-full flex-col lg:block" data-id="element-12" data-mp="1gfax3rjey9bi">
        <div className="relative order-2 flex-1 overflow-hidden lg:absolute lg:inset-0 lg:order-none" data-id="element-13" data-mp="12k8j0h4exdnm">
          <motion.img src={ABOUT_IMAGE} alt="An ensemble of dancers in sheer black dresses on a checkered floor beneath two beams of light" loading="lazy" style={prefersReducedMotion ? undefined : {
          y
        }} className="h-64 w-full object-cover object-bottom sm:h-96 lg:h-full" data-id="element-14" data-mp="16lbzn5uabdpw" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-ink via-ink/50 to-transparent lg:bg-gradient-to-b lg:from-ink lg:via-ink/85 lg:to-transparent" data-id="element-15" data-mp="1ssxu310cbauo" />
        </div>

        <div className="relative order-1 z-10 px-5 py-16 sm:px-8 lg:order-none lg:flex lg:min-h-screen lg:max-w-[560px] lg:flex-col lg:justify-center lg:px-16 lg:py-0" data-id="element-16" data-mp="1j2v130t7xwwc">
          <Reveal data-id="element-17" data-mp="1a1okkrqkkm33">
            <p className="mb-4 text-[11px] uppercase tracking-[0.3em] text-ice" data-id="element-18" data-mp="1a15dk62gji3u">About</p>
            <div className="space-y-5" data-id="element-19" data-mp="151sfep64yoly">
              <p className="text-xl leading-snug text-led sm:text-2xl" data-id="element-20" data-mp="152u2xt4c93kk">{PARAGRAPHS[0]}</p>
              <p className="text-sm leading-relaxed text-led/75 sm:text-base" data-id="element-21" data-mp="104ispha4sr1t">{PARAGRAPHS[1]}</p>
              <p className="text-sm leading-relaxed text-led/75 sm:text-base" data-id="element-22" data-mp="2zf1tyvdjh0b2">{PARAGRAPHS[2]}</p>
            </div>
          </Reveal>
        </div>
      </div>

      <div className="px-5 py-20 sm:px-8 lg:px-12 lg:py-28" data-id="element-23" data-mp="32yiqw0tj68ph">
        <div className="mx-auto max-w-3xl" data-id="element-24" data-mp="32y0m2fssym5y">
          <Reveal data-id="element-25" data-mp="36zm3mega7omh">
            <p className="mb-10 text-center text-[11px] uppercase tracking-[0.3em] text-ice" data-id="element-26" data-mp="36ysvpa5l6t2r">Credits</p>
          </Reveal>

          <Reveal delay={0.1} data-id="element-27" data-mp="3br4656xydx9k">
            <div data-id="element-28" data-mp="3bsx5s2a5fh82">
              <p className="mb-6 text-center text-[10px] uppercase tracking-[0.25em] text-led/50" data-id="element-29" data-mp="3gpe39n38n7xt">
                {CREATIVE_TEAM.title}
              </p>
              <dl className="space-y-3" data-id="element-30" data-mp="2k17ssjjmxlbm">
                {CREATIVE_TEAM.entries.map(entry => <div key={entry.role} className="grid grid-cols-2 items-baseline gap-6" data-id="element-31" data-mp="2o19ugspdeeqw">
                    <dt className="text-right text-[11px] uppercase tracking-[0.15em] text-led/50" data-id="element-32" data-mp="2syi6ebjxdmjp">
                      {entry.role}
                    </dt>
                    <dd className="text-left text-sm text-led sm:text-base" data-id="element-33" data-mp="8ado1tp3jtri">{entry.name}</dd>
                  </div>)}
              </dl>
            </div>
          </Reveal>

          <Reveal delay={0.15} data-id="element-34" data-mp="1m5lwp9waz9wz">
            <p className="my-12 text-center font-heading text-xl uppercase tracking-tight text-led sm:text-2xl" data-id="element-35" data-mp="1m6oorl5d3ieh">
              {PRODUCTION_STATEMENT}
            </p>
          </Reveal>

          <Reveal delay={0.2} data-id="element-36" data-mp="1v6tulquwq8qj">
            <div className="border-t border-led/10 pt-10" data-id="element-37" data-mp="1v7v8e33b59by">
              <p className="mb-6 text-center text-[10px] uppercase tracking-[0.25em] text-led/50" data-id="element-38" data-mp="1q77ixluq90ju">
                {PRODUCTION.title}
              </p>
              <dl className="space-y-3" data-id="element-39" data-mp="252n411dbxm43">
                {PRODUCTION.entries.map(entry => <div key={entry.role} className="grid grid-cols-2 items-baseline gap-6" data-id="element-40" data-mp="203kc8imd1ce2">
                    <dt className="text-right text-[11px] uppercase tracking-[0.15em] text-led/50" data-id="element-41" data-mp="2dw9x2jq5kb9l">
                      {entry.role}
                    </dt>
                    <dd className="text-left text-sm text-led sm:text-base" data-id="element-42" data-mp="2nug1b7zflthm">{entry.name}</dd>
                  </div>)}
              </dl>
            </div>
          </Reveal>

          <Reveal delay={0.25} data-id="element-43" data-mp="32heq0v6wm9sn">
            <p className="mt-12 text-center" data-id="element-44" data-mp="32j971yf21hrg">
              <a href={FULL_CREDITS_PDF_URL} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-[0.2em] text-led/60 underline decoration-led/30 underline-offset-4 transition-colors duration-300 hover:text-ice focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice" data-id="element-45" data-mp="37h0ykrbsf1v4">
                Full credits — Monopol Berlin, August 2026
              </a>
            </p>
          </Reveal>
        </div>
      </div>
    </section>;
}